import type { Sighting, SightingType, SightingStatus, DetectionSource, SightingStats, SightingAction } from "../types";

const sightingTypes: SightingType[] = [
  "earth_moving", "road_works", "building_works", "machinery_parked",
  "agi_activity", "marker_posts", "forestry", "quarry", "flooding",
  "ditching", "network_works", "fencing", "other", "unidentified"
];

const statuses: SightingStatus[] = ["open", "dispatched", "closed"];
const sources: DetectionSource[] = ["node", "helicopter", "manual", "third_party"];
const zones = ["SN1", "SN2", "SN3", "SN4", "SN5", "W1", "W2", "W3", "W4", "W5", "E1", "E2", "E3", "E4", "E5"];

const locations = [
  "A45 Junction near Coventry",
  "B4114 Bedworth Road",
  "M6 Corridor Section 12",
  "Hinckley Road Industrial Estate",
  "Nuneaton Bypass",
  "Rugby Eastern Relief Road",
  "Lutterworth Feeder Station Area",
  "Market Harborough Pipeline Crossing",
  "Daventry AGI Compound",
  "Northampton Western Bypass",
  "Kettering Industrial Zone",
  "Corby Steel Works Perimeter",
  "Wellingborough River Crossing",
  "Rushden Lakes Development Area",
  "A14 Cambridge Junction",
];

const technicians = [
  "James Wilson", "Sarah Mitchell", "David Brown", "Emma Thompson",
  "Michael Davis", "Lisa Anderson", "Robert Taylor", "Jennifer White"
];

const generateMapReference = (): string => {
  const letters = ["SP", "SK", "TL", "TF", "SE", "NZ"];
  const letter = letters[Math.floor(Math.random() * letters.length)];
  const easting = Math.floor(Math.random() * 90000 + 10000);
  const northing = Math.floor(Math.random() * 90000 + 10000);
  return `${letter} ${easting} ${northing}`;
};

const generateRefNumber = (zone: string, index: number): string => {
  const year = "26";
  const week = String(Math.floor(Math.random() * 52) + 1).padStart(2, "0");
  const seq = String(index).padStart(4, "0");
  return `${year}${week}S${zone} ${seq}`;
};

const generateCoordinates = (): [number, number] => {
  // UK coordinates around Midlands
  const lng = -1.5 + (Math.random() * 1);
  const lat = 52.0 + (Math.random() * 0.8);
  return [lng, lat];
};

const generateSighting = (index: number): Sighting => {
  const zone = zones[Math.floor(Math.random() * zones.length)];
  const status = statuses[Math.floor(Math.random() * statuses.length)];
  const type = sightingTypes[Math.floor(Math.random() * sightingTypes.length)];
  const source = sources[Math.floor(Math.random() * sources.length)];

  const now = new Date();
  const daysAgo = Math.floor(Math.random() * 30);
  const reportedDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);

  const sighting: Sighting = {
    id: `WO-${String(2024000 + index).padStart(7, "0")}`,
    referenceNumber: generateRefNumber(zone, index),
    type,
    status,
    source,
    sourceId: source === "node" ? `NODE-${Math.floor(Math.random() * 13000)}` :
              source === "helicopter" ? `PATROL-${Math.floor(Math.random() * 1000)}` : undefined,
    mapReference: generateMapReference(),
    location: locations[Math.floor(Math.random() * locations.length)],
    coordinates: generateCoordinates(),
    zone,
    description: getTypeDescription(type),
    technician: status !== "open" ? technicians[Math.floor(Math.random() * technicians.length)] : undefined,
    techAware: status === "dispatched" || status === "closed",
    dateReported: reportedDate.toISOString(),
    dispatchedAt: status !== "open" ? new Date(reportedDate.getTime() + 2 * 60 * 60 * 1000).toISOString() : undefined,
    closedAt: status === "closed" ? new Date(reportedDate.getTime() + 24 * 60 * 60 * 1000).toISOString() : undefined,
    notes: generateNotes(status),
    actions: generateActions(status, reportedDate),
  };

  return sighting;
};

const getTypeDescription = (type: SightingType): string => {
  const descriptions: Record<SightingType, string[]> = {
    earth_moving: [
      "Excavator observed digging near pipeline marker",
      "Trenching activity detected within 50m of pipeline corridor",
      "Ground disturbance observed, possible unauthorized excavation",
    ],
    road_works: [
      "Road resurfacing works in progress on pipeline crossing",
      "Traffic management barriers placed near pipeline route",
      "Surface repairs being conducted near AGI access road",
    ],
    building_works: [
      "New construction foundations being laid adjacent to pipeline",
      "Commercial development activity near pipeline easement",
      "Residential building works observed within corridor",
    ],
    machinery_parked: [
      "Excavator and plant machinery parked overnight near marker post",
      "Construction equipment staged near pipeline crossing",
      "Heavy machinery positioned within proximity zone",
    ],
    agi_activity: [
      "Unscheduled vehicle activity at AGI compound",
      "Personnel observed at AGI outside normal hours",
      "Equipment being moved within AGI perimeter",
    ],
    marker_posts: [
      "Aerial marker post found damaged, requires replacement",
      "Marker post obscured by vegetation growth",
      "Marker post knocked down, possibly by vehicle impact",
    ],
    forestry: [
      "Tree felling activity within pipeline corridor",
      "Forestry operations near pipeline easement",
      "Vegetation clearing works in progress",
    ],
    quarry: [
      "Quarry expansion activity detected near pipeline",
      "Extraction works observed within monitoring zone",
      "New quarry access road being constructed",
    ],
    flooding: [
      "Localised flooding reported near pipeline crossing",
      "Water accumulation observed at low point on route",
      "Drainage failure causing waterlogging near marker",
    ],
    ditching: [
      "Drainage ditch being excavated parallel to pipeline",
      "Agricultural ditching works in progress",
      "Ditch maintenance activity near pipeline crossing",
    ],
    network_works: [
      "Scheduled NTS maintenance in progress",
      "Pipeline inspection team on site",
      "Cathodic protection survey being conducted",
    ],
    fencing: [
      "New fencing being installed near pipeline easement",
      "Agricultural fencing works within corridor",
      "Security fencing contractor on site near AGI",
    ],
    other: [
      "Unclassified activity requiring investigation",
      "General activity detected, needs manual review",
      "Activity type to be determined by field team",
    ],
    unidentified: [
      "Unknown activity detected by sensors",
      "Unidentified movement within monitoring zone",
      "Activity requiring field verification",
    ],
  };

  const options = descriptions[type];
  return options[Math.floor(Math.random() * options.length)];
};

const generateNotes = (status: SightingStatus) => {
  if (status === "open") return [];

  const notes = [
    {
      id: "note-1",
      author: "Control Room",
      content: "Initial assessment complete. Activity appears to be within safe distance but monitoring required.",
      timestamp: new Date().toISOString(),
    },
  ];

  if (status === "closed") {
    notes.push({
      id: "note-2",
      author: technicians[Math.floor(Math.random() * technicians.length)],
      content: "Site visit completed. No immediate risk to pipeline. Activity verified as authorized.",
      timestamp: new Date().toISOString(),
    });
  }

  return notes;
};

const generateActions = (status: SightingStatus, reportedDate: Date) => {
  const actions: SightingAction[] = [
    {
      id: "action-1",
      type: "create",
      actor: "System",
      description: "Sighting created from detection",
      timestamp: reportedDate.toISOString(),
    },
  ];

  if (status !== "open") {
    actions.push({
      id: "action-2",
      type: "dispatch",
      actor: "Control Room",
      description: "Work order dispatched to field technician",
      timestamp: new Date(reportedDate.getTime() + 30 * 60 * 1000).toISOString(),
    });
    actions.push({
      id: "action-3",
      type: "tech_aware",
      actor: technicians[Math.floor(Math.random() * technicians.length)],
      description: "Technician acknowledged assignment",
      timestamp: new Date(reportedDate.getTime() + 45 * 60 * 1000).toISOString(),
    });
  }

  if (status === "closed") {
    actions.push({
      id: "action-4",
      type: "close",
      actor: technicians[Math.floor(Math.random() * technicians.length)],
      description: "Site verified, no action required. Sighting closed.",
      timestamp: new Date(reportedDate.getTime() + 24 * 60 * 60 * 1000).toISOString(),
    });
  }

  return actions;
};

// Generate 60 sightings
export const sightingsData: Sighting[] = Array.from({ length: 60 }, (_, i) => generateSighting(i + 1));

export const getSightingById = (id: string): Sighting | undefined => {
  return sightingsData.find((s) => s.id === id);
};

export const getSightingStats = (): SightingStats => {
  const byType = {} as Record<SightingType, number>;
  const bySource = {} as Record<DetectionSource, number>;

  sightingTypes.forEach((type) => {
    byType[type] = sightingsData.filter((s) => s.type === type).length;
  });

  sources.forEach((source) => {
    bySource[source] = sightingsData.filter((s) => s.source === source).length;
  });

  return {
    total: sightingsData.length,
    open: sightingsData.filter((s) => s.status === "open").length,
    dispatched: sightingsData.filter((s) => s.status === "dispatched").length,
    closed: sightingsData.filter((s) => s.status === "closed").length,
    byType,
    bySource,
  };
};
